import { useEffect, useState } from "react";
import { Pin, PinOff, ArrowUp, ArrowDown, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, asset, formatApiError } from "@/lib/api";

const parseCode = (v) => {
  const m = v.trim().match(/instagram\.com\/(?:[^/]+\/)?(?:p|reel|tv)\/([A-Za-z0-9_-]+)/);
  return m ? m[1] : /^[A-Za-z0-9_-]{5,40}$/.test(v.trim()) ? v.trim() : null;
};

const move = (arr, i, d) => {
  const j = i + d;
  if (j < 0 || j >= arr.length) return arr;
  const out = [...arr];
  [out[i], out[j]] = [out[j], out[i]];
  return out;
};

export const InstagramPicker = ({ pinned, setPinned, onlyPinned, setOnlyPinned }) => {
  const [latest, setLatest] = useState([]);
  const [info, setInfo] = useState({});
  const [link, setLink] = useState("");
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    api.get("/instagram/latest").then((r) => {
      const posts = Array.isArray(r.data?.posts) ? r.data.posts : [];
      setLatest(posts);
      setInfo((s) => ({ ...Object.fromEntries(posts.map((p) => [p.shortcode, p])), ...s }));
    }).catch(() => setLatest([]));
  }, []);

  useEffect(() => {
    pinned.filter((c) => !info[c]).forEach((c) => {
      api.get(`/instagram/post/${c}`).then((r) => setInfo((s) => ({ ...s, [c]: r.data }))).catch(() => setInfo((s) => ({ ...s, [c]: { missing: true } })));
    });
  }, [pinned, info]);

  const toggle = (code) => setPinned(pinned.includes(code) ? pinned.filter((c) => c !== code) : [...pinned, code]);

  const add = async () => {
    const code = parseCode(link);
    if (!code) return toast.error("Paste a link like instagram.com/p/XXXX/");
    if (pinned.includes(code)) return toast.error("Already pinned");
    setAdding(true);
    try {
      const { data } = await api.get(`/instagram/post/${code}`);
      setInfo((s) => ({ ...s, [code]: data }));
      setPinned([...pinned, code]);
      setLink("");
      toast.success("POST PINNED — REMEMBER TO SAVE");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="ig-picker">
      <p className="text-sm text-zinc-400">Pinned posts show first in the Latest slideshow, in the order below. Tap a recent post to pin it, or paste a link to pin any older post.</p>

      <div>
        <span className="block font-mono text-[11px] tracking-[0.25em] text-zinc-400 mb-2">RECENT POSTS</span>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {latest.map((p) => {
            const on = pinned.includes(p.shortcode);
            return (
              <button type="button" key={p.shortcode} data-testid={`ig-recent-${p.shortcode}`} onClick={() => toggle(p.shortcode)}
                className={`relative aspect-square overflow-hidden border-2 transition-[border-color,opacity] ${on ? "border-[#F5A524]" : "border-transparent opacity-60 hover:opacity-100"}`}>
                <img src={asset(p.image)} alt="" className="w-full h-full object-cover" />
                <span className={`absolute top-1 right-1 p-1 ${on ? "bg-[#F5A524] text-black" : "bg-black/70 text-zinc-300"}`}>{on ? <Pin size={12} /> : <PinOff size={12} />}</span>
              </button>
            );
          })}
          {latest.length === 0 && <p className="col-span-full font-mono text-xs text-zinc-400">Couldn't load recent posts right now.</p>}
        </div>
      </div>

      <div className="flex gap-2">
        <input data-testid="ig-link-input" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://www.instagram.com/p/..."
          className="flex-1 bg-[#0B0C10] border border-[#F5A524]/30 focus:border-[#F5A524] outline-none px-3 py-2.5 text-sm text-white" />
        <button type="button" data-testid="ig-link-add-btn" onClick={add} disabled={adding}
          className="flex items-center gap-2 px-4 bg-[#F5A524] text-black font-mono text-[11px] font-bold tracking-[0.2em] hover:bg-white disabled:opacity-60">
          <Plus size={14} /> {adding ? "CHECKING..." : "PIN LINK"}
        </button>
      </div>

      <div>
        <span className="block font-mono text-[11px] tracking-[0.25em] text-zinc-400 mb-2">PINNED ({pinned.length})</span>
        <div className="space-y-2">
          {pinned.map((c, i) => (
            <div key={c} data-testid={`ig-pinned-${c}`} className="flex items-center gap-3 bg-[#0B0C10] border border-[#F5A524]/20 p-2">
              <span className="font-mono text-xs text-[#F5A524] w-6 text-center">{i + 1}</span>
              {info[c]?.image ? <img src={asset(info[c].image)} alt="" className="w-12 h-12 object-cover" /> : <span className="w-12 h-12 bg-[#12131A]" />}
              <p className="flex-1 min-w-0 text-sm text-zinc-300 truncate">{info[c]?.missing ? "Post unavailable" : info[c]?.caption || c}</p>
              <button type="button" data-testid={`ig-pinned-up-${c}`} onClick={() => setPinned(move(pinned, i, -1))} aria-label="Move up" className="p-2 text-zinc-300 hover:text-[#F5A524]"><ArrowUp size={14} /></button>
              <button type="button" data-testid={`ig-pinned-down-${c}`} onClick={() => setPinned(move(pinned, i, 1))} aria-label="Move down" className="p-2 text-zinc-300 hover:text-[#F5A524]"><ArrowDown size={14} /></button>
              <button type="button" data-testid={`ig-pinned-remove-${c}`} onClick={() => toggle(c)} aria-label="Unpin" className="p-2 text-[#FF2A55] hover:bg-[#FF2A55] hover:text-black"><PinOff size={14} /></button>
            </div>
          ))}
          {pinned.length === 0 && <p className="font-mono text-xs text-zinc-400 border border-dashed border-[#F5A524]/20 p-4">NO PINS — THE SLIDESHOW SHOWS YOUR 6 MOST RECENT POSTS.</p>}
        </div>
      </div>

      <label className="flex items-center gap-3 cursor-pointer">
        <input data-testid="ig-only-pinned-checkbox" type="checkbox" checked={onlyPinned} onChange={(e) => setOnlyPinned(e.target.checked)} className="w-4 h-4 accent-[#F5A524]" />
        <span className="font-mono text-xs tracking-[0.2em] text-zinc-300">SHOW ONLY PINNED POSTS (HIDE THE REST)</span>
      </label>
    </div>
  );
};
